import { Injectable, OnDestroy } from "@angular/core";
import { TermDataModel, TermViewModel, TermConfirmationViewModel } from "../../shared/models/term.model";
import { Observable } from "rxjs";
import { ImpressumDataModel, ImpressumTermViewModel, ImpressumDMKey } from "../../shared/models/impressum.model";
import { environment } from "../../../../environments/environment";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { ContentType } from "../../shared/contentTypes";
import { IObjectObserver } from "../../services/IObjectObserver";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { TermDialogComponent } from "../../modalDialogs/termDialog/termDialog.component";
import { BottomSheetHelperService } from "../../services/bottomSheetHelper.service";
import { lng } from '../../languages/de.language';
import { lng as lngEN } from '../../languages/en.language';

@Injectable({
  providedIn: "root"
})
export class TermService {
  private _impressumObsServ: IObjectObserver<ImpressumDataModel>;
  private _termObsServ: IObjectObserver<TermDataModel>;

  constructor(objObsInjectHelper: CircularInjectionHelper
    , private _bottomSheetHelperService: BottomSheetHelperService) {
    this._impressumObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<ImpressumDataModel>(ContentType.Impressum, objObsInjectHelper);
    this._termObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<TermDataModel>(ContentType.Term, objObsInjectHelper);
  }

  /**
   * Getting the list of Impressum and Term DataModel
   * Create ImpressumTermViewmodel
   * @result return a promise that resolves a data of ImpressumTermViewModel
   */
  public GetImpressumTermViewModel(): Promise<ImpressumTermViewModel[]> {
    const me = this;
    return new Promise<ImpressumTermViewModel[]>((resolve, reject) => {
      const impressumTermViewModel: ImpressumTermViewModel[] = new Array();
      me._impressumObsServ.GetObject(ImpressumDMKey)
        .then((impressum) => {
          impressumTermViewModel.push(me.mapImpressumDataModelToViewModel(impressum));
          // Add static Accessibility (Barrierefreiheit) entry with improved structure
          impressumTermViewModel.push(me.createAccessibilityTermViewModel());
          me._termObsServ.GetObjectsByFilter(model => model.release && !model.deleted)
            .then((terms) => {
              impressumTermViewModel.push(...me.mapImpressumDataModelToImpressumTermViewModelList(terms));
              resolve(impressumTermViewModel);
            })
            .catch(reject);
        })
        .catch(reject);
    });
  }

  public get ImpressumObserveChanges(): Observable<(string | number)[]> {
    return this._impressumObsServ.ObserveChanges;
  }

  public get TermObserveChanges(): Observable<(string | number)[]> {
    return this._termObsServ.ObserveChanges;
  }

  // public GetAllReleasedTermConfirmationViewModels(): Promise<TermConfirmationViewModel[]> {
  //   const me = this;
  //   return new Promise<TermConfirmationViewModel[]>((resolve, reject) => {
  //     const termConfirmationViewModel: TermConfirmationViewModel[] = [];
  //     return me._termObsServ.GetObjectsByFilter(t => !t.deleted && t.release).then((terms) => {
  //       termConfirmationViewModel.push(...me.mapToTermConfirmationViewModelList(terms));
  //       resolve(termConfirmationViewModel);
  //     }).catch(reject);
  //   });
  // }

  public GetTermConfirmationViewModels(): Promise<TermConfirmationViewModel[]> {
    const me = this;
    return new Promise<TermConfirmationViewModel[]>((resolve, reject) => {
      const termConfirmationViewModel: TermConfirmationViewModel[] = [];
      return me._termObsServ.GetObjectsByFilter(t => t.deleted === false).then((terms) => {
        termConfirmationViewModel.push(...me.mapToTermConfirmationViewModelList(terms));
        resolve(termConfirmationViewModel);
      }).catch(reject);
    });
  }

  public GetTermConfirmationViewModelByIds(ids: number[]): Promise<TermConfirmationViewModel[]> {
    const me = this;
    return new Promise<TermConfirmationViewModel[]>((resolve, reject) => {
      const termConfirmationViewModel: TermConfirmationViewModel[] = [];
      return me._termObsServ.GetObjectsByFilter(t => ids.includes(t.id)).then((terms) => {
        termConfirmationViewModel.push(...me.mapToTermConfirmationViewModelList(terms));
        resolve(termConfirmationViewModel);
      }).catch(reject);
    });
  }

  /**
   * open a modal dialog to show term detail
   * @param termId id of the term to show
   */
  public openTermModel(termId: number): void {
    const me = this;
    me._bottomSheetHelperService.open(TermDialogComponent, {
      data: termId,
      panelClass: "mat-bottom-sheet-container--full-height"
    });
  }

  // Mapper

  /** map a TermDataModelList to TermViewModelList
 * @param model Get a list of TermDataModel for mapping
 * @result return a list of TermViewModel
 */
  private mapTermDataModelListToViewModelList(modelList: TermDataModel[]): TermViewModel[] {
    const me = this;
    const viewModeList: TermViewModel[] = [];
    modelList.forEach(model => {
      viewModeList.push(me.mapTermDataModelToViewModel(model));
    });
    return viewModeList;
  }

  /**map a TermDataModel to TermViewModel
   * @param model Get a TermDataModel for mapping
   * @result return a TermViewModel
   */
  private mapTermDataModelToViewModel(model: TermDataModel): TermViewModel {
    const me = this;
    const viewModel = new TermViewModel();
    viewModel.id = model.id;
    viewModel.sort_nr = model.sort_nr;
    viewModel.label_de = model.label_de;
    viewModel.label_en = model.label_en;
    viewModel.text_html_de = model.text_html_de;
    viewModel.text_html_en = model.text_html_en;
    return viewModel;
  }

  /** map a TermDataModelList to TermConfirmationViewModelList
  * @param model Get a list of TermDataModel for mapping
  * @result return a list of TermConfirmationViewModel
  */
  private mapToTermConfirmationViewModelList(modelList: TermDataModel[]): TermConfirmationViewModel[] {
    const me = this;
    const viewModeList: TermConfirmationViewModel[] = [];
    modelList.forEach(model => {
      viewModeList.push(me.mapToTermConfirmationViewModel(model));
    });
    return viewModeList;
  }

  /**map a TermDataModel to TermConfirmationViewModel
   * @param model Get a TermDataModel for mapping
   * @result return a TermConfirmationViewModel
   */
  private mapToTermConfirmationViewModel(model: TermDataModel): TermConfirmationViewModel {
    const me = this;
    const viewModel = new TermConfirmationViewModel();
    viewModel.id = model.id;
    viewModel.label_de = model.label_de;
    viewModel.label_en = model.label_en;
    viewModel.versionYMD = model.version_ymd;
    viewModel.versionHMS = model.version_hms;
    return viewModel;
  }

  /**
 * map a ImpressumDataModel to ImpressumViewModel
 * @param model Get a ImpressumDataModel for mapping
 * @result return a ImpressumViewModel
 */
  private mapImpressumDataModelToViewModel(model: ImpressumDataModel): ImpressumTermViewModel {
    const me = this;
    const viewModel = new ImpressumTermViewModel();
    viewModel.id = -1;
    viewModel.labelDE = "Impressum";
    viewModel.labelEN = "Legal Notice";
    viewModel.termHtmlContentDE = model.text_html_de;
    viewModel.termHtmlContentEN = model.text_html_en;
    viewModel.sortNumber = -1;
    return viewModel;
  }

  /**
 * map a TermDataModel to ImpressumTermViewModel
 * @param model Get a ImpressumTermViewModel for mapping
 * @result return a ImpressumTermViewModel
 */
  private mapImpressumDataModelToImpressumTermViewModelList(modelList: TermDataModel[]): ImpressumTermViewModel[] {
    const me = this;
    const result: ImpressumTermViewModel[] = [];
    if (modelList && modelList.length > 0) {
      modelList.forEach(model => {
        result.push(me.mapImpressumDataModelToImpressumTermViewModel(model));
      });
      return result;
    } else {
      return null;
    }

  }
  /**
   * map a TermDataModel to ImpressumTermViewModel
   * @param model Get a ImpressumTermViewModel for mapping
   * @result return a ImpressumTermViewModel
   */
  private mapImpressumDataModelToImpressumTermViewModel(model: TermDataModel): ImpressumTermViewModel {
    const viewModel = new ImpressumTermViewModel();
    viewModel.id = model.id;
    viewModel.labelDE = model.label_de;
    viewModel.labelEN = model.label_en;
    viewModel.termHtmlContentDE = model.text_html_de;
    viewModel.termHtmlContentEN = model.text_html_en;
    viewModel.sortNumber = model.sort_nr;
    return viewModel;
  }

  private createAccessibilityTermViewModel(): ImpressumTermViewModel {
    const contentKeys: string[] = [
      "TITLE", "SECTION1_TITLE", "SECTION1_TEXT", "SECTION2_TITLE", "SECTION2_TEXT1", "SECTION2_TEXT2", "SECTION2_LIST",
      "SECTION3_TITLE", "SECTION3_TEXT", "SECTION4_TITLE", "SECTION4_TEXT1", "SECTION4_LIST1", "SECTION4_TEXT2", "SECTION4_LIST2",
      "SECTION4_SUBTITLE1", "SECTION4_SUBTITLE1_TEXT", "SECTION4_SUBTITLE2", "SECTION4_SUBTITLE2_TEXT",
      "SECTION4_SUBTITLE3", "SECTION4_SUBTITLE3_TEXT", "SECTION4_SUBTITLE4", "SECTION4_SUBTITLE4_TEXT",
      "SECTION5_TITLE", "SECTION5_TEXT1", "SECTION5_TEXT2", "SECTION5_LIST1", "SECTION5_TEXT3", "SECTION5_LIST2",
      "SECTION5_TEXT4", "SECTION5_LIST3", "SECTION5_TEXT5", "SECTION6_TITLE", "SECTION6_TEXT1", "SECTION6_LIST1",
      "SECTION6_TEXT2", "SECTION6_LIST2", "SECTION7_TITLE", "SECTION7_TEXT1", "SECTION7_CONTACT", "SECTION8_TITLE", "SECTION8_CONTACT"
    ];
    const getHtml = (lngObj: any, key: string): string => {
      const value = lngObj[`phTERMS_ACCESSIBILITY_${key}`];
      if (Array.isArray(value)) {
        return `<ul>${value.map(item => `<li>${item}</li>`).join('')}</ul>`;
      }
      if (key.includes('TITLE') && !key.includes('SUBTITLE')) {
        const level = key === 'TITLE' ? '1' : '2';
        return `<h${level}>${value}</h${level}>`;
      }
      if (key.includes('SUBTITLE') && !key.includes('_TEXT')) {
        return `<h3>${value}</h3>`;
      }
      if (key.includes('CONTACT')) {
        return `<p>${value.replace(/\n/g, '<br>')}</p>`;
      }
      return `<p>${value}</p>`;
    };
    const htmlDE: string = contentKeys.map(key => getHtml(lng, key)).join('');
    const htmlEN: string = contentKeys.map(key => getHtml(lngEN, key)).join('');
    const labelDE: string = lng["phTERMS_ACCESSIBILITY_TITLE"].replace(/<.*?>/g, "");
    const labelEN: string = "Accessibility";
    return Object.assign(new ImpressumTermViewModel(), {
      id: -2,
      labelDE,
      labelEN,
      termHtmlContentDE: htmlDE,
      termHtmlContentEN: htmlEN,
      sortNumber: 0
    });
  }
}
